import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Loader2, Lock, FileText } from 'lucide-react';
import { getStatusClass, formatFullTimestamp } from '../utils/proposalHelpers';
import api from '../../../services/api';

/**
 * Helper to check if assignment status indicates closed/expired.
 */
const isClosedAssignment = (statusCode) => {
    return statusCode === 56 || statusCode === 57;
};

/**
 * Helper to get the display label for closed assignment.
 */
const getClosedAssignmentLabel = (statusCode) => {
    if (statusCode === 56) return "Expired";
    if (statusCode === 57) return "Assignment Closed";
    return "Assignment Closed";
};

/**
 * AssignmentRowView — extracted from MyProposals.jsx (Phase 7 refactor).
 * Strictly for team members. Uses the new grouped API schema.
 * Supports status mapping and custom UI states for expired & auto-closed assignments.
 */
const AssignmentRowView = ({ prop }) => {
    const navigate = useNavigate();
    const rfpId = prop.rfp_id || prop._id || prop.id;
    const title = prop.proposal_title || 'Untitled Proposal';
    const client = prop.client_name || 'Not Specified';

    // Status mapping & checks
    const statusCode = Number(prop.global_status?.code || prop.status_code || prop.status || prop.statusCode);
    const isClosed = isClosedAssignment(statusCode);
    const closedLabel = getClosedAssignmentLabel(statusCode);

    // Tooltip configuration
    const tooltipText = statusCode === 56 
        ? "Expired assignment — no further action is required."
        : "Automatically closed assignment — available for summary view only.";

    // Progress: completed_sections / total_sections
    const completed = prop.completed_sections ?? 0;
    const total = prop.total_sections ?? (prop.sections?.length ?? 0);
    const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;
    const hasNoSections = total === 0;
    const progressText = hasNoSections ? 'No sections available' : `${completed}/${total} sections`;

    // Assigned at — use latest_assigned_at from new schema
    const rawAssigned = prop.latest_assigned_at || prop.created_at;
    const assignedAt = rawAssigned ? formatFullTimestamp(rawAssigned) : '—';

    const [isViewing, setIsViewing] = React.useState(false);

    // Directly call GET /rfp/employee/{rfp_id}/my-sections — unchanged
    const handleView = async (e) => {
        if (e) e.stopPropagation();
        if (isViewing || !rfpId) return;
        setIsViewing(true);
        try {
            const res = await api.get(`/rfp/employee/${rfpId}/my-sections`);
            const fullData = res.status === 200 ? res.data : prop;
            
            // For closed assignments, pass read-only states in navigation
            navigate(`/proposals/analysis-result/${rfpId}`, {
                state: { 
                    rfpId, 
                    analysisData: fullData, 
                    fromProposals: true,
                    isReadOnly: isClosed,
                    isClosed: isClosed
                }
            });
        } catch (err) {
            console.error('Failed to fetch my-sections:', err);
            navigate(`/proposals/analysis-result/${rfpId}`, {
                state: { 
                    rfpId, 
                    analysisData: prop, 
                    fromProposals: true,
                    isReadOnly: isClosed,
                    isClosed: isClosed
                }
            });
        } finally {
            setIsViewing(false);
        }
    };

    return (
        <div
            className={`assignment-row ${isClosed ? 'assignment-row-closed' : ''}`}
            onClick={handleView}
            style={{ 
                cursor: isViewing ? 'not-allowed' : 'pointer',
                opacity: isClosed ? 0.8 : 1,
                backgroundColor: isClosed ? '#F8F9FA' : undefined,
                transition: 'all 0.2s ease-in-out'
            }}
        >
            {/* Col 1: Proposal & Client */}
            <div className="row-col col-main">
                <div className="prop-info-title" style={{ color: isClosed ? '#475569' : undefined }}>
                    {title}
                </div>
                <div className="prop-info-client" style={{ color: isClosed ? '#94A3B8' : undefined }}>
                    {client}
                </div>
            </div>

            {/* Col 3: Progress (completed / total sections) */}
            <div className="row-col col-sections" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div className="assignment-progress-wrap" style={{ width: '100%' }}>
                    <div className="assignment-progress-bar">
                        <div
                            className="assignment-progress-fill"
                            style={{ 
                                width: `${progressPct}%`,
                                backgroundColor: isClosed ? '#94A3B8' : undefined
                            }}
                        />
                    </div>
                    <span 
                        className="assignment-progress-label"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                        <span>{progressText}</span>
                        {isClosed && (
                            <span 
                                title={tooltipText}
                                style={{ 
                                    cursor: 'help',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: '#64748B'
                                }}
                            >
                                <Lock size={12} />
                            </span>
                        )}
                    </span>
                </div>
                {isClosed && (
                    <div 
                        style={{ 
                            fontSize: '12px', 
                            color: '#64748B', 
                            fontWeight: '500', 
                            marginTop: '4px',
                            textAlign: 'left'
                        }}
                    >
                        {closedLabel}
                    </div>
                )}
            </div>

            {/* Col 4: Latest Assigned At */}
            <div className="row-col col-assigned">
                <span className="assigned-text">{assignedAt}</span>
            </div>

            {/* Col 5: View / View Summary button */}
            <div className="row-col col-actions">
                {!isClosed ? (
                    <button
                        className="assignment-view-btn"
                        onClick={handleView}
                        disabled={isViewing}
                        title="View assigned sections"
                    >
                        {isViewing
                            ? <Loader2 size={14} className="animate-spin" />
                            : <Eye size={14} />}
                        <span>{isViewing ? 'Loading...' : 'View'}</span>
                    </button>
                ) : (
                    <button
                        className="assignment-view-summary-btn"
                        onClick={handleView}
                        disabled={isViewing}
                        title="View summary of closed assignment"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: '#F1F5F9',
                            border: '1px solid #E2E8F0',
                            borderRadius: '6px',
                            color: '#475569',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: isViewing ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isViewing ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <FileText size={14} />
                        )}
                        <span>{isViewing ? 'Loading...' : 'View Summary'}</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default AssignmentRowView;
